<template>
  <div>
    <div v-for="(action, idx) in actions" :key="action.id" :class="action.incentivetype" class="action">
      <div class="updown icons">
        <a
          :class="defaultButtonClasses.concat(['btn-link'])"
          :disabled="idx <= 0"
          href="#"
          @click.prevent="moveUp(actions, idx)"
        >
          <span class="fa fa-chevron-up" />
          <span class="sr-only">Move Action Up</span>
        </a>
        <a
          :class="defaultButtonClasses.concat(['btn-link'])"
          :disabled="idx >= actions.length - 1"
          href="#"
          @click.prevent="moveDown(actions, idx)"
        >
          <span class="fa fa-chevron-down" />
          <span class="sr-only">Move Action Down</span>
        </a>
      </div>
      <div class="body">
        <div class="float-right">
          <a
            :class="defaultButtonClasses.concat(['btn-primary'])"
            href="#"
            @click.prevent="addAction(actions, idx)"
          >
            <span class="fa fa-plus" />
            <span class="sr-only">Add Action</span>
          </a>
          <a
            v-if="actions.length > 1 || action.incentivetype != '' "
            :class="defaultButtonClasses.concat(['btn-danger'])"
            href="#"
            @click.prevent="removeItem(actions, idx)"
          >
            <span class="fa fa-trash" />
            <span class="sr-only">Delete Action</span>
          </a>
        </div>
        <select v-model="action.incentivetype" class="form-control" @change="feedback.updateActionProps(action)">
          <option value="">
            Select action
          </option>
          <option v-for="(label, value) in config.actionChoices" :value="value">
            {{ label }}
          </option>
        </select>
        <component :is="action.incentivetype" v-bind.sync="action" :config="config" />
      </div>
    </div>
  </div>
</template>
<script>
  import { v4 as uuidV4 } from 'uuid';

  // Actions
  import AddPoints from './actions/AddPoints.vue';
  import assignArm from './actions/assignArm.vue';
  import assignStrata from './actions/assignStrata.vue';
  import assignStudyGroup from './actions/assignStudyGroup.vue';
  import unassignStudyGroup from './actions/unassignStudyGroup.vue';
  import ClassifyData from './actions/ClassifyData.vue';
  import CloseEventBlock from './actions/CloseEventBlock.vue';
  import compliant from './actions/compliant.vue';
  import createCohort from './actions/createCohort.vue';
  import credit from './actions/credit.vue';
  import debit from './actions/debit.vue';
  import DisableMessaging from './actions/DisableMessaging.vue';
  import EnableMessaging from './actions/EnableMessaging.vue';
  import FinishParticipant from './actions/FinishParticipant.vue';
  import incident from './actions/incident.vue';
  import ineligible from './actions/ineligible.vue';
  import declined from './actions/declined.vue';
  import message from './actions/message.vue';
  import ModifyListVariable from './actions/ModifyListVariable.vue';
  import ModifyDateVariable from './actions/ModifyDateVariable.vue';
  import ModifyNumericVariable from './actions/ModifyNumericVariable.vue';
  import ModifyTextBasedVariable from './actions/ModifyTextBasedVariable.vue';
  import ModifyProfile from './actions/ModifyProfile.vue';
  import ModifySupportPartnerProfile from './actions/ModifySupportPartnerProfile.vue';
  import CreateSupportPartnerProfile from './actions/CreateSupportPartnerProfile.vue';
  import ModifySupportPartnerStatus from './actions/ModifySupportPartnerStatus.vue';
  import noncompliant from './actions/noncompliant.vue';
  import PopulateDeviceConfiguration from './actions/PopulateDeviceConfiguration.vue';
  import ReopenEncounter from './actions/ReopenEncounter.vue';
  import ScheduleStart from './actions/ScheduleStart.vue';
  import SetLocale from './actions/SetLocale.vue';
  import ScheduleEventBlock from './actions/ScheduleEventBlock.vue';
  import SetPoints from './actions/SetPoints.vue';
  import SubtractPoints from './actions/SubtractPoints.vue';
  import unenrollment from './actions/unenrollment.vue';
  import StartConversation from './actions/StartConversation.vue';
  import StartSupportPartnerConversation from './actions/StartSupportPartnerConversation.vue';
  import CloseConversation from './actions/CloseConversation.vue';
  import CloseSupportPartnerConversation from './actions/CloseSupportPartnerConversation.vue';
  import ResolveIncidents from './actions/ResolveIncidents.vue';
  import StartEvent from "./actions/StartEvent.vue";
  import SkipEvent from "./actions/SkipEvent.vue";
  import EpicOrder from "./actions/EpicOrder.vue";

export default {
  components: {
    // Actions:
    AddPoints,
    ClassifyData,
    CloseEventBlock,
    FinishParticipant,
    ModifyListVariable,
    ModifyDateVariable,
    // Same vue component but different displays based off tag
    ModifyParticipantGoalVariable: ModifyNumericVariable,
    ModifyPointBalanceVariable: ModifyNumericVariable,
    ModifyTextBasedVariable,
    ModifyProfile,
    ModifySupportPartnerProfile,
    CreateSupportPartnerProfile,
    ModifySupportPartnerStatus,
    PopulateDeviceConfiguration,
    ReopenEncounter,
    ScheduleStart,
    SetLocale,
    ScheduleEventBlock,
    SetPoints,
    SubtractPoints,
    StartConversation,
    StartSupportPartnerConversation,
    CloseConversation,
    CloseSupportPartnerConversation,
    assignArm,
    assignStrata,
    assignStudyGroup,
    unassignStudyGroup,
    compliant,
    createCohort,
    credit,
    debit,
    incident,
    ineligible,
    declined,
    message,
    DisableMessaging,
    EnableMessaging,
    noncompliant,
    unenrollment,
    ResolveIncidents,
    StartEvent,
    SkipEvent,
    EpicOrder
  },
  props: {
    header: String,
    help: String,
    actions: {
      type: Array,
      default: () => []
    },
    config: {
      type: Object
    },
    defaultButtonClasses: {
      type: Array
    },
    feedback: {
      type: Object, // should really be type: Feedback but that causes an import dependency cycle
    },
  },
  methods: {
    // NOTE: the next few methods actually are mutating the `actions` list
    // passed as a prop

    // TODO: some of these are duplicates of what's in BaseApp or EventLogic.
    // We should consolidate somehow.
    addAction: function(list, after) {
      list.splice(after + 1, 0, { id: uuidV4(), incentivetype: '' });
    },
    removeItem: function (list, idx) {
      list.splice(idx, 1);
    },
    moveUp: function (list, idx) {
      list.splice(idx - 1, 0, list.splice(idx, 1)[0]);
    },
    moveDown: function (list, idx) {
      list.splice(idx + 1, 0, list.splice(idx, 1)[0]);
    },
    validate: function () {
      var valid = true;
      this.$children.forEach(function (component) {
        if (component.hasOwnProperty('validate')) {
          valid = component.validate() && valid;
        }
      });
      return valid;
    },
  },
}
</script>
